/**
 * Conversational Workflow Builder Component
 * Chat-based interface for building workflows through conversation
 */
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  MessageSquare, Send, Sparkles, RefreshCw, 
  CheckCircle, AlertCircle, Loader2, Bot, User, Mic, MicOff
} from 'lucide-react';
import { toast } from 'sonner';
import agenticApi from '../../api/agenticApi';

const ConversationalBuilder = ({ headers, onWorkflowComplete }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startSession = async () => {
    setIsLoading(true);
    try {
      const response = await agenticApi.startWorkflowChat(headers);
      
      setMessages([{
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date().toISOString()
      }]);
      setSessionStarted(true);
      
    } catch (err) {
      console.error('Session start error:', err);
      toast.error(err.response?.data?.detail || 'Failed to start session');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      const response = await agenticApi.chatWorkflow({
        message: input.trim(),
        history: messages.map(m => ({ role: m.role, content: m.content })),
        language: 'en'
      }, headers);
      
      const assistantMessage = {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date().toISOString(),
        status: response.data.status,
        workflow: response.data.workflow,
        requires_confirmation: response.data.requires_confirmation
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.data.workflow) {
        setCurrentWorkflow(response.data.workflow);
      }
      
      if (response.data.status === 'complete' && response.data.requires_confirmation) {
        toast.info('Workflow ready! Please confirm to create it.');
      }
      
    } catch (err) {
      console.error('Chat error:', err);
      toast.error(err.response?.data?.detail || 'Failed to send message');
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmWorkflow = async () => {
    if (!currentWorkflow) return;
    
    setIsLoading(true);
    try {
      // Step 1: Generate workflow structure through AI
      const description = `${currentWorkflow.name}: ${currentWorkflow.description}`;
      const documentTypes = currentWorkflow.document_types || [];
      const genResponse = await agenticApi.generateWorkflow(
        description,
        documentTypes,
        'en',
        headers
      );
      
      const suggestion = genResponse.data?.suggestion || genResponse.data?.preview || currentWorkflow;
      
      // Step 2: Actually persist the workflow via the workflows API
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('reasondms_token');
      
      const workflowPayload = {
        name: suggestion.name || currentWorkflow.name || 'AI-Generated Workflow',
        json_definition: {
          steps: suggestion.steps || currentWorkflow.steps || [],
          document_types: suggestion.document_types || documentTypes,
          escalation_enabled: suggestion.escalation_enabled ?? true,
          notifications: suggestion.notifications || {}
        },
        is_active: true
      };
      
      const createResponse = await fetch(`${API_URL}/api/workflows/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(headers || {})
        },
        body: JSON.stringify(workflowPayload)
      });
      
      if (!createResponse.ok) {
        const errData = await createResponse.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to save workflow');
      }
      
      const createdWorkflow = await createResponse.json();
      
      toast.success('Workflow created successfully!');
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Workflow "${createdWorkflow.name}" has been created and is now active! You can find it in the Definitions tab.`,
        timestamp: new Date().toISOString()
      }]);
      
      if (onWorkflowComplete) {
        onWorkflowComplete(createdWorkflow);
      }
      
      // Reset for new session
      setCurrentWorkflow(null);
      
    } catch (err) {
      console.error('Workflow creation error:', err);
      toast.error(err.message || err.response?.data?.detail || 'Failed to create workflow');
    } finally {
      setIsLoading(false);
    }
  };

  const resetSession = () => {
    setMessages([]);
    setCurrentWorkflow(null);
    setSessionStarted(false);
    stopListening();
  };

  // Voice input using Web Speech API
  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Speech recognition is not supported in this browser');
      return;
    }
    
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => {
      setIsListening(true);
    };
    
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput(transcript);
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone access.');
      } else if (event.error !== 'aborted') {
        toast.error('Voice input error. Please try again.');
      }
      setIsListening(false);
    };
    
    recognition.onend = () => {
      setIsListening(false);
    };
    
    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  };

  const toggleVoice = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  if (!sessionStarted) {
    return (
      <Card className="h-[500px] flex flex-col" data-testid="conversational-builder">
        <CardContent className="flex-1 flex flex-col items-center justify-center gap-4">
          <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">AI Workflow Builder</h3>
          <p className="text-slate-600 text-center max-w-md">
            Build document approval workflows through natural conversation. 
            Describe what you need and I'll help you create it.
          </p>
          <Button
            onClick={startSession}
            disabled={isLoading}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="start-chat-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <MessageSquare className="w-4 h-4 mr-2" />
                Start Conversation
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-[500px] flex flex-col" data-testid="conversational-builder">
      <CardHeader className="py-3 border-b flex-shrink-0">
        <CardTitle className="text-base flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            AI Workflow Builder
          </div>
          <Button variant="ghost" size="sm" onClick={resetSession}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      
      {/* Messages */}
      <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : msg.isError
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : 'bg-slate-100 text-slate-800'
              }`}
            >
              <div className="flex items-start gap-2">
                {msg.role === 'assistant' && (
                  <Bot className="w-4 h-4 mt-1 flex-shrink-0" />
                )}
                <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
              </div>
              
              {/* Show workflow preview */}
              {msg.workflow && (
                <div className="mt-3 p-2 bg-white rounded border text-slate-700">
                  <p className="font-medium text-sm">{msg.workflow.name}</p>
                  <p className="text-xs text-slate-500">{msg.workflow.steps?.length || 0} steps</p>
                </div>
              )}
              
              {/* Confirmation button */}
              {msg.requires_confirmation && currentWorkflow && (
                <Button
                  size="sm"
                  className="mt-3 bg-green-600 hover:bg-green-700"
                  onClick={confirmWorkflow}
                  disabled={isLoading}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Confirm & Create
                </Button>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-lg p-3">
              <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </CardContent>
      
      {/* Input */}
      <div className="p-3 border-t flex-shrink-0">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder={isListening ? 'Listening...' : 'Describe your workflow...'}
            disabled={isLoading}
            className={isListening ? 'border-red-300 bg-red-50' : ''}
            data-testid="workflow-chat-input"
          />
          <Button
            onClick={toggleVoice}
            variant={isListening ? 'destructive' : 'outline'}
            disabled={isLoading}
            title={isListening ? 'Stop listening' : 'Start voice input'}
            data-testid="voice-input-btn"
          >
            {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        {isListening && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            Listening... Speak your workflow description
          </p>
        )}
      </div>
    </Card>
  );
};

export default ConversationalBuilder;
